import React, { useState } from "react";
import { useUpdateParticipantInfo } from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Clock, User } from "lucide-react";
import { useVisualViewport } from "@/hooks/use-visual-viewport";
import { isValidEmail } from "@/utils/validation";

function LogoMark() {
  return (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="16" cy="16" r="15" stroke="hsl(262 83% 58%)" strokeWidth="2" fill="none"/>
      <circle cx="16" cy="16" r="9" stroke="hsl(262 83% 58%)" strokeWidth="2" fill="none" opacity="0.5"/>
      <circle cx="16" cy="16" r="3.5" fill="hsl(262 83% 58%)"/>
      <path d="M16 1.5 A14.5 14.5 0 0 1 30.5 16" stroke="hsl(262 83% 58%)" strokeWidth="2.5" strokeLinecap="round"/>
    </svg>
  );
}

export interface ParticipantInfoGateProps {
  assessmentToken: string;
  onComplete: () => void;
  existingName?: string;
  existingEmail?: string;
}

export function ParticipantInfoGate({ assessmentToken, onComplete, existingName, existingEmail }: ParticipantInfoGateProps) {
  const showName = !existingName;
  const showEmail = !existingEmail;

  const [name, setName] = useState(existingName ?? "");
  const [email, setEmail] = useState(existingEmail ?? "");
  const [errors, setErrors] = useState<{ name?: string; email?: string }>({});
  const [apiError, setApiError] = useState<string | null>(null);

  const mutation = useUpdateParticipantInfo();
  const vp = useVisualViewport();

  const validate = () => {
    const errs: { name?: string; email?: string } = {};
    if (showName && !name.trim()) errs.name = "Nome obrigatório";
    if (showEmail) {
      if (!email.trim()) errs.email = "E-mail obrigatório";
      else if (!isValidEmail(email)) errs.email = "E-mail inválido";
    }
    return errs;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setApiError(null);
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setErrors({});
    mutation.mutate(
      { assessmentToken, data: { participantName: name.trim(), participantEmail: email.trim() } },
      {
        onSuccess: () => onComplete(),
        onError: (err: unknown) => {
          const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
          setApiError(msg ?? "Erro ao salvar informações. Tente novamente.");
        },
      }
    );
  };

  const title = showName && showEmail
    ? "Antes de começar"
    : showName
      ? "Qual é o seu nome?"
      : "Qual é o seu e-mail?";

  const subtitle = showName && showEmail
    ? "Preencha seus dados para iniciar a avaliação."
    : showName
      ? "Informe seu nome completo para iniciar a avaliação."
      : "Informe seu e-mail para iniciar a avaliação.";

  const containerStyle: React.CSSProperties = vp
    ? {
        position: "fixed",
        top: `${vp.offsetTop}px`,
        left: 0,
        right: 0,
        height: `${vp.height}px`,
      }
    : { minHeight: "100dvh" };

  return (
    <div
      className="flex items-center justify-center bg-background px-4 py-6 sm:p-6 overflow-y-auto"
      style={containerStyle}
    >
      <div className="max-w-md w-full">
        <div className="flex items-center justify-center gap-2.5 mb-8">
          <LogoMark />
          <span className="text-base font-semibold text-foreground tracking-tight">Avaliação Comportamental</span>
        </div>
        <Card className="w-full shadow-sm border border-border rounded-xl">
          <CardHeader className="text-center pb-2 pt-7">
            <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
              <User className="h-6 w-6 text-primary" />
            </div>
            <CardTitle className="text-lg font-semibold">{title}</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              {subtitle}
            </p>
            <div className="flex items-center justify-center gap-1.5 mt-3">
              <span className="inline-flex items-center gap-1 text-xs text-muted-foreground bg-muted px-2.5 py-1 rounded-full">
                <Clock className="h-3 w-3" />
                ~10 minutos
              </span>
            </div>
          </CardHeader>
          <CardContent className="pb-7">
            <form onSubmit={handleSubmit} className="space-y-4 pt-3">
              {showName && (
                <div className="space-y-1.5">
                  <Label htmlFor="participant-name" className="text-sm font-medium">Nome completo</Label>
                  <Input
                    id="participant-name"
                    placeholder="Seu nome completo"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="h-11 rounded-lg bg-white border-border text-base sm:text-sm"
                    autoComplete="name"
                    data-testid="input-participant-name"
                  />
                  {errors.name && <p className="text-xs text-destructive">{errors.name}</p>}
                </div>
              )}

              {showEmail && (
                <div className="space-y-1.5">
                  <Label htmlFor="participant-email" className="text-sm font-medium">E-mail</Label>
                  <Input
                    id="participant-email"
                    type="email"
                    placeholder="seu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-11 rounded-lg bg-white border-border text-base sm:text-sm"
                    autoComplete="email"
                    data-testid="input-participant-email"
                  />
                  {errors.email && <p className="text-xs text-destructive">{errors.email}</p>}
                </div>
              )}

              {apiError && <p className="text-sm text-destructive">{apiError}</p>}

              <Button
                type="submit"
                className="w-full h-12 rounded-lg font-medium mt-1 text-base sm:text-sm sm:h-10"
                disabled={mutation.isPending}
                data-testid="button-submit-participant-info"
              >
                {mutation.isPending ? "Salvando..." : "Iniciar avaliação"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
